import { useEffect, useState } from "react"

export interface CrossfadeSlide {
  url: string
  alt_text: string | null
}

interface Props {
  slides: CrossfadeSlide[]
  /** aria-label for the nth dot, 1-based. */
  dotLabel: (n: number) => string
  intervalMs?: number
  className?: string
  /** Off when the slideshow is a background: the film above it would swallow
      the dots, and a focusable control under an aria-hidden layer is worse
      than none. */
  showDots?: boolean
}

// Admin-managed photo slideshow: stacked images crossfaded via opacity with
// clickable dot indicators. The rotation interval only starts in useEffect, so
// SSR deterministically renders slide 0 and hydration matches.
export default function PhotoCrossfade({ slides, dotLabel, intervalMs = 5500, className, showDots = true }: Props) {
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (slides.length <= 1) return
    const timer = setInterval(() => setActiveIndex(i => (i + 1) % slides.length), intervalMs)
    return () => clearInterval(timer)
  }, [slides.length, intervalMs])

  return (
    <div className={className}>
      {slides.map((slide, i) => (
        <img
          key={slide.url}
          src={slide.url}
          alt={slide.alt_text ?? ""}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000"
          style={{ opacity: i === activeIndex ? 1 : 0 }}
          loading={i === 0 ? "eager" : "lazy"}
          decoding={i === 0 ? "sync" : "async"}
          fetchPriority={i === 0 ? "high" : "low"}
        />
      ))}
      {showDots && slides.length > 1 && (
        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActiveIndex(i)}
              className={`h-2.5 w-2.5 rounded-full transition-opacity duration-300 ${
                i === activeIndex ? "bg-white" : "bg-white/40 hover:bg-white/70"
              }`}
              aria-label={dotLabel(i + 1)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
